import React, { useMemo, useState } from "react";
import { Alert, Button, Form, Spinner } from "react-bootstrap";
import { useTranslation, TranslationParams } from "../i18n/I18nProvider";

interface QuoteOtpStepProps {
  onSend: (channels: { sendSms: boolean; sendEmail: boolean }) => Promise<void>;
  onVerify: (code: string) => Promise<void>;
  isSending: boolean;
  isVerifying: boolean;
  otpSent: boolean;
  smsSent: boolean;
  emailSent: boolean;
  error?: string | null;
  leadEmail?: string | null;
  leadPhone?: string | null;
  translationPrefix?: string;
}

export const QuoteOtpStep: React.FC<QuoteOtpStepProps> = ({
  onSend,
  onVerify,
  isSending,
  isVerifying,
  otpSent,
  smsSent,
  emailSent,
  error,
  leadEmail,
  leadPhone,
  translationPrefix,
}) => {
  const { t } = useTranslation();
  const [sendSms, setSendSms] = useState(Boolean(leadPhone));
  const [sendEmail, setSendEmail] = useState(Boolean(leadEmail));
  const [code, setCode] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);
  const prefix = translationPrefix ?? "identity.otp";
  const translate = (key: string, params?: TranslationParams) =>
    t(`${prefix}.${key}`, params);

  const canSend = useMemo(() => {
    return (sendSms || sendEmail) && !isSending;
  }, [sendSms, sendEmail, isSending]);

  const handleSend = async (event: React.FormEvent) => {
    event.preventDefault();
    setLocalError(null);

    if (!sendSms && !sendEmail) {
      setLocalError(translate("channelsRequired"));
      return;
    }

    try {
      await onSend({ sendSms, sendEmail });
    } catch (err: any) {
      setLocalError(err?.message ?? t("wizard.errors.generic"));
    }
  };

  const handleVerify = async (event: React.FormEvent) => {
    event.preventDefault();
    setLocalError(null);

    if (!code.trim()) {
      setLocalError(translate("codeRequired"));
      return;
    }

    try {
      await onVerify(code.trim());
    } catch (err: any) {
      setLocalError(err?.message ?? t("wizard.errors.generic"));
    }
  };

  const renderStatus = () => {
    if (!otpSent) return null;

    const messages = [];
    if (smsSent) {
      messages.push(translate("smsSent", { phone: leadPhone ?? "" }));
    }
    if (emailSent) {
      messages.push(translate("emailSent", { email: leadEmail ?? "" }));
    }

    if (messages.length === 0) {
      return null;
    }

    return (
      <Alert variant="success" className="mt-3">
        {messages.map((msg, index) => (
          <div key={index}>{msg}</div>
        ))}
      </Alert>
    );
  };

  return (
    <div className="card">
      <div className="card-body">

        {/* Banner motivacional — reduce friction percibida */}
        <div className="otp-context-banner">
          <span className="otp-context-icon">🔐</span>
          <div>
            <p className="otp-context-title">
              {translate("contextTitle") || (prefix === "identity.otp"
                ? "Verificamos tu identidad para mostrarte planes exactos"
                : "Confirmación de seguridad")}
            </p>
            <p className="otp-context-desc">
              {translate("contextDesc") || "Este paso protege tu información y garantiza que los planes y precios que verás son precisamente los que corresponden a tu perfil. Solo toma 30 segundos."}
            </p>
          </div>
        </div>

        {/* Mini steps preview */}
        <div className="otp-steps-preview">
          <div className="otp-step-item">
            <span className="otp-step-num">1</span>
            {translate("step1") || "Elige cómo recibir el código"}
          </div>
          <span className="otp-divider">→</span>
          <div className="otp-step-item">
            <span className="otp-step-num">2</span>
            {translate("step2") || "Ingresa el código de 6 dígitos"}
          </div>
          <span className="otp-divider">→</span>
          <div className="otp-step-item">
            <span className="otp-step-num">3</span>
            {translate("step3") || "Ver tus planes disponibles"}
          </div>
        </div>

        <h3 className="card-title">{translate("title")}</h3>
        <p className="section-helper">{translate("helper")}</p>

        {(localError || error) && (
          <Alert variant="danger">{localError || error}</Alert>
        )}

        <Form onSubmit={handleSend} className="mb-4">
          <Form.Group controlId="sendSms">
            <Form.Check
              type="switch"
              label={
                leadPhone
                  ? translate("sendSmsWithPhone", { phone: leadPhone })
                  : translate("sendSms")
              }
              disabled={!leadPhone || isSending}
              checked={sendSms && Boolean(leadPhone)}
              onChange={(event) => setSendSms(event.target.checked)}
            />
          </Form.Group>

          <Form.Group controlId="sendEmail">
            <Form.Check
              type="switch"
              label={
                leadEmail
                  ? translate("sendEmailWithAddress", { email: leadEmail })
                  : translate("sendEmail")
              }
              disabled={!leadEmail || isSending}
              checked={sendEmail && Boolean(leadEmail)}
              onChange={(event) => setSendEmail(event.target.checked)}
            />
          </Form.Group>

          <Button type="submit" className="button button-primary" disabled={!canSend}>
            {isSending ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                {translate("sending")}
              </>
            ) : (
              translate("sendCode")
            )}
          </Button>
        </Form>

        {renderStatus()}

        {otpSent && (
          <Form onSubmit={handleVerify}>
            <Form.Group controlId="otpCode" className="mb-3">
              <Form.Label>{translate("codeLabel")}</Form.Label>
              <Form.Control
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={code}
                onChange={(event) => setCode(event.target.value)}
                placeholder="000000"
                className="otp-code-input"
              />
            </Form.Group>

            <Button
              type="submit"
              className="button button-primary"
              disabled={isVerifying}
            >
              {isVerifying ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  {translate("verifying")}
                </>
              ) : (
                translate("verifyButton")
              )}
            </Button>
          </Form>
        )}
      </div>
    </div>
  );
};
